import { useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useAuth, useCan } from '@/auth/context'
import { displayName } from '@/auth/authApi'
import { useFormat } from '@/i18n'
import { istDateKey } from '@/lib/ist'
import { Badge, Banner, EmptyState } from '@/ui'
import { CalendarCheck } from '@phosphor-icons/react'
import { ck } from '../api/keys'
import { fetchExceptions, SEVERITIES } from '../api/exceptions'
import { fetchPayouts } from '../api/payouts'
import { getPage } from '../api/core'
import { mapStock } from '../api/store'
import { fetchLots, fetchSessions } from '../api/gate'
import { LIVE_REFRESH_MS } from '../api/openDeliveries'
import { ConsolePage } from '../components/Layout'
import { Check, Kg, Section, StatTile, When } from '../components/bits'
import { useExceptions } from '../api/exceptions'
import { useOpenFailures } from './payouts/useOpenFailures'
import { useScope } from '../api/appConfig'

const count = (p: Promise<{ total: number }>) => p.then((r) => r.total)

/** Today at a glance: the gate, money, exceptions and the store. */
export function DashboardPage() {
  const can = useCan()
  const { v1 } = useScope()
  const { user } = useAuth()
  const fmt = useFormat()
  const today = istDateKey()
  const [now] = useState(() => Date.now())

  const sessionsToday = useQuery({
    queryKey: [...ck.dashboard, 'sessions', today],
    enabled: can('collection-sessions:read'),
    refetchInterval: LIVE_REFRESH_MS,
    queryFn: () => fetchSessions({ from: today, to: today, page: 1, limit: 100 }),
  })
  const openSessions = useQuery({
    queryKey: [...ck.dashboard, 'sessions-open'],
    enabled: can('collection-sessions:read'),
    refetchInterval: LIVE_REFRESH_MS,
    queryFn: () => fetchSessions({ status: 'OPEN', page: 1, limit: 8 }),
  })
  const held = useQuery({
    queryKey: [...ck.dashboard, 'lots-held'],
    enabled: can('units:read'),
    refetchInterval: LIVE_REFRESH_MS,
    queryFn: () => count(fetchLots({ hold: 'QUARANTINE', page: 1, limit: 1 })),
  })
  const payouts = useQueries({
    queries: (['PENDING_APPROVAL', 'FAILED', 'READY'] as const).map((status) => ({
      queryKey: [...ck.dashboard, 'payouts', status],
      enabled: can('payouts:read'),
      refetchInterval: LIVE_REFRESH_MS,
      queryFn: () => count(fetchPayouts({ status, page: 1, limit: 1 })),
    })),
  })
  const exceptions = useQueries({
    queries: SEVERITIES.map((severity) => ({
      queryKey: [...ck.dashboard, 'exceptions', severity],
      enabled: can('exceptions:read'),
      refetchInterval: LIVE_REFRESH_MS,
      queryFn: () => count(fetchExceptions({ status: 'OPEN', severity, page: 1, limit: 1 })),
    })),
  })
  const stock = useQuery({
    queryKey: [...ck.dashboard, 'stock'],
    enabled: can('stock:read'),
    queryFn: () => getPage('/stock', { page: 1, limit: 200 }, mapStock),
  })

  // Process and pack: page totals only (limit 1)
  const total = (path: string, filters: object) =>
    count(getPage(path, { ...filters, page: 1, limit: 1 }, (r) => r))
  const runCounts = useQueries({
    queries: (['OPEN', 'RUNNING', 'EXCEPTION'] as const).map((status) => ({
      queryKey: [...ck.dashboard, 'runs', status],
      enabled: can('process-runs:read'),
      queryFn: () => total('/process-runs', { status }),
    })),
  })
  const releaseWaiting = useQuery({
    queryKey: [...ck.dashboard, 'release-waiting'],
    enabled: can('pack-lots:read'),
    queryFn: () => total('/release-queue', { state: 'WAITING' }),
  })
  const packHeld = useQuery({
    queryKey: [...ck.dashboard, 'release-held'],
    enabled: can('pack-lots:read'),
    queryFn: () => total('/release-queue', { state: 'HELD' }),
  })
  const labPending = useQuery({
    queryKey: [...ck.dashboard, 'lab-pending'],
    enabled: can('lab-reports:read'),
    queryFn: () => total('/lab-reports', { status: 'ENTERED' }),
  })
  const [runsOpen, runsRunning, runsHeld] = runCounts

  const rows = sessionsToday.data?.rows ?? []
  const lotsToday = rows.reduce((s, r) => s + r.lotsMain + r.lotsConv, 0)
  const kgToday = rows.reduce((s, r) => s + r.netG, 0)
  const stockRows = stock.data?.rows ?? []
  const stockBags = stockRows.reduce((s, r) => s + r.bags, 0)
  const stockKg = stockRows.reduce((s, r) => s + r.kgNowG, 0)
  const stockHeld = stockRows.filter((r) => r.unitHold !== 'NONE' || r.bagsOnHold > 0).length
  const stockPartial = (stock.data?.total ?? 0) > stockRows.length

  return (
    <ConsolePage
      eyebrow={fmt.date(now)}
      title={`Good ${greeting()}${user ? `, ${displayName(user).split(' ')[0]}` : ''}`}
      description="Today at the farm gate, in the store, at the machines and in the pack hall. Every tile opens the list behind it."
    >
      {can('collection-sessions:read') && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatTile
            label="Days open now"
            value={openSessions.data?.total ?? 0}
            loading={openSessions.isLoading}
            to="/c/sessions?status=OPEN"
            tone="info"
          />
          <StatTile
            label="Lots today"
            value={lotsToday}
            hint={<Kg grams={kgToday} />}
            loading={sessionsToday.isLoading}
            to={`/c/sessions?from=${today}&to=${today}`}
          />
          <StatTile
            label="Lots on hold"
            value={held.data ?? 0}
            loading={held.isLoading}
            to="/c/lots?hold=QUARANTINE"
            tone={(held.data ?? 0) > 0 ? 'warn' : 'neutral'}
          />
          {v1 ? (
            <StatTile
              label="Records still arriving"
              value={
                rows.filter((r) => r.status !== 'OPEN' && r.allCommandsArrived === false).length
              }
              hint="days closed today · payments wait for them"
              loading={sessionsToday.isLoading}
              to="/c/sessions?status=CLOSED"
            />
          ) : (
            <StatTile
              label="Days not balanced"
              value={
                rows.filter(
                  (r) =>
                    r.status !== 'OPEN' && (r.tagsBalanced === false || r.bagsBalanced === false),
                ).length
              }
              hint="today"
              loading={sessionsToday.isLoading}
              to="/c/sessions?status=CLOSED"
            />
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {can('payouts:read') && (
          <Section
            title="Payments"
            actions={
              <Link className="text-[13px] underline" to="/c/payouts">
                All payouts
              </Link>
            }
          >
            <div className="grid grid-cols-3 gap-3">
              <StatTile
                label="Waiting approval"
                value={payouts[0]?.data ?? 0}
                loading={payouts[0]?.isLoading}
                to="/c/payouts?status=PENDING_APPROVAL"
                tone={(payouts[0]?.data ?? 0) > 0 ? 'warn' : 'neutral'}
              />
              <StatTile
                label="Ready to send"
                value={payouts[2]?.data ?? 0}
                loading={payouts[2]?.isLoading}
                to="/c/payouts?status=READY"
              />
              <OpenFailuresTile />
            </div>
            <LowBalanceNote />
          </Section>
        )}
        {can('exceptions:read') && (
          <Section
            title="Open exceptions"
            eyebrow="By severity"
            actions={
              <Link className="text-[13px] underline" to="/c/exceptions">
                Queue
              </Link>
            }
          >
            <div className="grid grid-cols-4 gap-3">
              {SEVERITIES.map((s, i) => (
                <StatTile
                  key={s}
                  label={s}
                  value={exceptions[i]?.data ?? 0}
                  loading={exceptions[i]?.isLoading}
                  to={`/c/exceptions?status=OPEN&severity=${s}`}
                  tone={
                    (exceptions[i]?.data ?? 0) > 0 && (s === 'CRITICAL' || s === 'HIGH')
                      ? 'danger'
                      : (exceptions[i]?.data ?? 0) > 0 && s === 'MEDIUM'
                        ? 'warn'
                        : 'neutral'
                  }
                />
              ))}
            </div>
          </Section>
        )}
      </div>

      {can('stock:read') && (
        <Section
          title="Stock on hand"
          actions={
            <Link className="text-[13px] underline" to="/c/stock">
              Stock
            </Link>
          }
        >
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatTile
              label="Lot · place rows"
              value={stock.data?.total ?? 0}
              loading={stock.isLoading}
            />
            <StatTile
              label="Bags"
              value={fmt.count(stockBags)}
              loading={stock.isLoading}
              hint={stockPartial ? 'first 200 rows' : undefined}
            />
            <StatTile
              label="kg now"
              value={<Kg grams={stockKg} />}
              loading={stock.isLoading}
              hint="last weighed, net of sacks"
            />
            <StatTile
              label="Rows on hold"
              value={stockHeld}
              loading={stock.isLoading}
              to="/c/stock?hold=true"
              tone={stockHeld > 0 ? 'warn' : 'neutral'}
            />
          </div>
        </Section>
      )}

      {(can('process-runs:read') || can('pack-lots:read') || can('lab-reports:read')) && (
        <Section title="Processing and packing" eyebrow="Machine runs · release · lab">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {can('process-runs:read') && (
              <>
                <StatTile
                  label="Runs open"
                  value={(runsOpen?.data ?? 0) + (runsRunning?.data ?? 0)}
                  loading={runsOpen?.isLoading || runsRunning?.isLoading}
                  hint={`${runsRunning?.data ?? 0} running`}
                  to="/c/runs?status=RUNNING"
                  tone="info"
                />
                <StatTile
                  label="Runs held"
                  value={runsHeld?.data ?? 0}
                  loading={runsHeld?.isLoading}
                  hint="waiting for a decision"
                  to="/c/runs?status=EXCEPTION"
                  tone={(runsHeld?.data ?? 0) > 0 ? 'warn' : 'neutral'}
                />
              </>
            )}
            {can('pack-lots:read') && (
              <StatTile
                label="Waiting for release"
                value={releaseWaiting.data ?? 0}
                loading={releaseWaiting.isLoading}
                hint={packHeld.data ? `${packHeld.data} pack lots on hold` : 'pack lots'}
                to="/c/release?state=WAITING"
                tone={(releaseWaiting.data ?? 0) > 0 ? 'info' : 'neutral'}
              />
            )}
            {can('lab-reports:read') && (
              <StatTile
                label="Lab reports to check"
                value={labPending.data ?? 0}
                loading={labPending.isLoading}
                hint="entered, not yet verified"
                to="/c/lab-reports?status=ENTERED"
                tone={(labPending.data ?? 0) > 0 ? 'warn' : 'neutral'}
              />
            )}
          </div>
        </Section>
      )}

      {can('collection-sessions:read') && (
        <Section title="Days open now" eyebrow="Collection days" bodyClassName="p-0">
          {openSessions.isError ? (
            <div className="p-3">
              <Banner tone="error">Could not load the open days.</Banner>
            </div>
          ) : (openSessions.data?.rows.length ?? 0) === 0 ? (
            <EmptyState
              compact
              icon={<CalendarCheck />}
              title="No day is open"
              description="Days appear here when an operator opens one on the phone."
            />
          ) : (
            <ul className="divide-y divide-rule/70">
              {openSessions.data!.rows.map((s) => (
                <li key={s.id}>
                  <Link
                    to={`/c/sessions/${s.id}`}
                    className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 hover:bg-paper-tint/60"
                  >
                    <span className="flex min-w-0 items-center gap-3">
                      <span className="font-mono text-[13px]">{s.code}</span>
                      <span className="truncate text-[14px]">
                        {s.villageName ?? '—'} · {s.operatorName ?? '—'}
                      </span>
                    </span>
                    <span className="flex items-center gap-3 text-[13px] text-muted">
                      <span>{s.lotsMain + s.lotsConv} lots</span>
                      <Kg grams={s.netG} />
                      {s.otherOperatorOpenSameDay && <Badge tone="warn">2 operators open</Badge>}
                      <Check ok={s.allCommandsArrived} />
                      <When at={s.openedAt} relative />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Section>
      )}
    </ConsolePage>
  )
}

function greeting(): string {
  const h = Number(
    new Intl.DateTimeFormat('en-IN', {
      hour: 'numeric',
      hour12: false,
      timeZone: 'Asia/Kolkata',
    }).format(new Date()),
  )
  return h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening'
}

/** Failed or returned payments still owed (a try already paid again is not counted), as on Payouts. */
function OpenFailuresTile() {
  const f = useOpenFailures()
  const n = f.items.length
  return (
    <StatTile
      label="Failed or returned"
      value={n}
      loading={f.isLoading}
      to="/c/payouts/failed"
      tone={n > 0 ? 'danger' : 'neutral'}
    />
  )
}

/** RazorpayX short of money: payments queue until the central team adds funds. */
function LowBalanceNote() {
  const low = useExceptions({ type: 'LOW_BALANCE', status: 'OPEN', page: 1, limit: 1 })
  const n = low.data?.total ?? 0
  if (!n) return null
  return (
    <Banner tone="warn" className="mt-3" title="RazorpayX balance is low">
      {n} payment{n === 1 ? ' is' : 's are'} queued until money is added.{' '}
      <Link className="underline" to="/c/payouts">
        Open Payouts
      </Link>
    </Banner>
  )
}
